/**
 * A built-in role that a plugin-contributed permission should be granted
 * to by default. The host resolves the target role row as
 * `builtin-${scope}-${roleSlug}` (matching the seeded built-in roles in
 * `server/src/auth/permissions.ts`) and inserts the grant idempotently at
 * boot.
 */
export interface PermissionRoleGrant {
  /** `'org'` for organization member roles, `'team'` for team roles. */
  scope: 'org' | 'team';
  /** Built-in role slug, e.g. `'owner'`, `'admin'`, `'member'`, `'viewer'`, `'maintainer'`. */
  roleSlug: string;
}

/**
 * Build a flat {@link PermissionRoleGrant} list from a compact
 * `{ org, team }` spec. Plugins use this to declare `defaultRoleGrants`
 * on a contributed permission without repeating the `{ scope, roleSlug }`
 * shape for every role:
 *
 * ```ts
 * defaultRoleGrants: roleGrants({
 *   org: ['owner', 'admin', 'member', 'viewer', 'billing'],
 *   team: ['maintainer', 'member'],
 * })
 * ```
 */
export function roleGrants(spec: {
  org?: readonly string[];
  team?: readonly string[];
}): PermissionRoleGrant[] {
  return [
    ...(spec.org ?? []).map((roleSlug) => ({ scope: 'org' as const, roleSlug })),
    ...(spec.team ?? []).map((roleSlug) => ({ scope: 'team' as const, roleSlug })),
  ];
}

/**
 * One row in the permission catalog. Plugins contribute additional rows
 * through `PluginServerDefinition.permissions`; the host merges them
 * with this base at boot and exposes the result via
 * `/permissions/catalog`.
 *
 * `category` is a free-form string so plugins can introduce their own
 * grouping label (`'projects'`, `'leaderboard'`, …) without a host
 * enum change. The web role-permissions picker uses
 * {@link PERMISSION_CATEGORY_LABELS} as a label map with a Title-Case
 * fallback for unknown categories.
 */
export interface PermissionCatalogEntry {
  slug: string;
  category: string;
  description: string;
  /**
   * Whether holding this permission lets the caller change something.
   *
   * Declared, never inferred. The posture rules used to answer this by
   * looking at the slug's suffix, which quietly filed `members:invite`,
   * `plugins:install`, and `org:billing` as reads because they don't end
   * in `:write`. A slug that gates a mutation says so here, whatever it
   * is called.
   */
  mutates: boolean;
  /**
   * Built-in roles that should be granted this permission by default.
   * The host seeds these into `role_permissions` at boot so a plugin can
   * dictate which built-in roles can use the surfaces its slug gates,
   * without the host having to enumerate plugin slugs in
   * {@link BUILTIN_ROLE_DEFINITIONS}.
   *
   * Only meaningful for plugin-contributed entries — the host base
   * catalog wires its own grants through `BUILTIN_ROLE_DEFINITIONS`.
   * Grants are inserted idempotently, so adding a role here self-heals
   * existing deployments on the next boot. Custom (non-built-in) roles
   * are unaffected.
   */
  defaultRoleGrants?: readonly PermissionRoleGrant[];
}

/**
 * Static catalog of every permission known to the host base. Plugins
 * extend this catalog at boot via their `permissions` contribution —
 * the host merges the two before exposing the catalog to the UI /
 * runtime validators.
 */
export const PERMISSION_CATALOG: readonly PermissionCatalogEntry[] = [
  // Org administration
  {
    slug: 'org:read',
    category: 'org',
    description: 'View organization details and members',
    mutates: false,
  },
  {
    slug: 'org:manage',
    category: 'org',
    description: 'Rename org, change slug, delete',
    mutates: true,
  },
  { slug: 'org:billing', category: 'org', description: 'Manage billing and plan', mutates: true },
  {
    slug: 'members:invite',
    category: 'org',
    description: 'Invite users to the organization',
    mutates: true,
  },
  {
    slug: 'members:manage',
    category: 'org',
    description: 'Remove users, change their roles',
    mutates: true,
  },
  {
    slug: 'teams:read',
    category: 'org',
    description: 'List teams in the organization',
    mutates: false,
  },
  {
    slug: 'teams:manage',
    category: 'org',
    description: 'Create, rename, delete teams',
    mutates: true,
  },
  {
    slug: 'roles:manage',
    category: 'org',
    description: 'Create and assign custom roles',
    mutates: true,
  },
  {
    slug: 'service-accounts:read',
    category: 'org',
    description: 'View service accounts and their API keys',
    mutates: false,
  },
  {
    slug: 'service-accounts:write',
    category: 'org',
    description: 'Create service accounts and mint or revoke their API keys',
    mutates: true,
  },
  {
    slug: 'company:read',
    category: 'company',
    description:
      'View company-wide settings: the plan, every organization the company owns, and the identity and provisioning configuration they share',
    mutates: false,
  },
  {
    slug: 'company:manage',
    category: 'company',
    description:
      'Change company-wide settings for every organization in the company, including its shared identity and provisioning configuration',
    mutates: true,
  },
  // Agents
  {
    slug: 'agents:read',
    category: 'agents',
    description: 'View agents and their streams',
    mutates: false,
  },
  {
    slug: 'agents:write',
    category: 'agents',
    description: 'Create and configure agents',
    mutates: true,
  },
  {
    slug: 'agents:run',
    category: 'agents',
    description: 'Start agents and send messages',
    mutates: true,
  },
  {
    slug: 'agents:stop',
    category: 'agents',
    description: 'Interrupt, stop, or archive agents',
    mutates: true,
  },
  // Owner scoping is NOT a slug: reaching another user's agent requires an
  // explicit per-resource grant on that one agent (`resource_grants`), never
  // an org-wide role. Null-owner (legacy/system) and service-account-owned
  // agents stay org-shared and need only the base slugs above.
  // Blueprints (still host-owned today — blueprints are core).
  {
    slug: 'blueprints:read',
    category: 'blueprints',
    description: 'View blueprints',
    mutates: false,
  },
  {
    slug: 'blueprints:write',
    category: 'blueprints',
    description: 'Create and edit blueprints',
    mutates: true,
  },
  // Secrets (host-owned)
  {
    slug: 'secrets:read',
    category: 'secrets',
    description: 'View secret metadata',
    mutates: false,
  },
  {
    slug: 'secrets:manage',
    category: 'secrets',
    description: 'Create, rotate, delete secrets',
    mutates: true,
  },
  {
    slug: 'service-secrets:manage',
    category: 'secrets',
    description: 'Create and rotate operator-managed service-tier secrets (super-admin only)',
    mutates: true,
  },
  // Integrations (host-owned)
  {
    slug: 'integrations:read',
    category: 'integrations',
    description: 'View integration configs',
    mutates: false,
  },
  {
    // Connecting your own account (and acting through it) is a per-user
    // capability, distinct from configuring the org's integrations.
    slug: 'integrations:connect',
    category: 'integrations',
    description: 'Connect your own account to integrations',
    mutates: true,
  },
  {
    slug: 'integrations:manage',
    category: 'integrations',
    description: 'Configure integrations',
    mutates: true,
  },
  // The following slugs are contributed by their owning plugins at boot
  // through `PluginServerDefinition.permissions`. The host base does not
  // declare them so the catalog stays plugin-clean:
  //   mcp:*           — plugin-mcp
  //   gateways:*      — plugin-ai-gateway
  //   flows:*         — plugin-flows
  //   conversations:* — plugin-chat
  //   personas:*      — plugin-agent-persona
  //   projects:*      — plugin-projects
  //   tailscale:*     — plugin-tailscale
  //   themes:*        — plugin-themes
  //   marketing:*     — plugin-marketing
  // Per-org plugin installation
  {
    slug: 'plugins:read',
    category: 'org',
    description: 'View installed and available plugins for the organization',
    mutates: false,
  },
  {
    slug: 'plugins:install',
    category: 'org',
    description: 'Install or uninstall plugins for the organization',
    mutates: true,
  },
  {
    // Org-tier, like the two above: it gates the per-org plugin settings
    // route. `plugins:*` used to be split across the `org` and `admin`
    // categories, which put a slug and its implied read in different
    // groups of the roles picker.
    slug: 'plugins:manage',
    category: 'org',
    description: 'Configure installed plugins for the organization',
    mutates: true,
  },
  // No `feature-flags:manage`. A global flag is one value for the whole
  // deployment and a per-user override list joins users across every
  // organization, so `/flags` writes and `/flags/:key/overrides/*` are gated
  // on the platform-admin tier (`RouteScope.PlatformAdmin`) for the same
  // reason `/seeds/*` is (see below). The slug is retired in
  // `RETIRED_PERMISSION_SLUGS`, so `runSeeds` drops the catalog row and every
  // built-in or custom role grant that carried it.
  // Admin surfaces (super-admin only unless overridden)
  {
    slug: 'admin:read',
    category: 'admin',
    description: 'View admin dashboards',
    mutates: false,
  },
  { slug: 'admin:manage', category: 'admin', description: 'Perform admin actions', mutates: true },
  {
    slug: 'database:manage',
    category: 'admin',
    description: 'Browse and edit the database',
    mutates: true,
  },
  // No `seeds:manage`. Seed export/import moves a bundle of blueprints,
  // personas, flows, secrets, and the org's access-control graph between
  // environments, so `/seeds/*` is gated on the platform-admin tier
  // (`RouteScope.PlatformAdmin`) rather than on an org-grantable slug. A slug
  // in this catalog is assignable to any custom role by an org admin holding
  // `roles:manage`, which is exactly the escalation the tier change closes —
  // so the slug is gone, not merely ungranted.
];

/**
 * Loose alias retained for callers that imported it. Categories are
 * free-form strings (plugins introduce their own), but the host base
 * still uses a stable set — surfaces that want a typed mapping for the
 * built-in categories can derive their own constant.
 */
export type PermissionCategory = string;

export const ALL_PERMISSION_SLUGS: readonly string[] = PERMISSION_CATALOG.map((p) => p.slug);

export function isValidPermissionSlug(perm: string): boolean {
  return ALL_PERMISSION_SLUGS.includes(perm);
}

/**
 * The one spelling a permission slug may have: `<domain>:<action>`, both
 * halves lower-case kebab (`service-accounts:read`, `service-secrets:manage`).
 *
 * The catalog used to mix kebab and snake for the same job
 * (`service_accounts:write` next to `service-secrets:manage`), which meant
 * no single pattern described it and slug schemas rejected real slugs.
 * Resource-kind names are kebab-only, so kebab is the spelling that lets a
 * kind and its domain share one word.
 */
export const PERMISSION_SLUG_PATTERN =
  /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*:[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

/**
 * The three standard actions. Verbs are first-class vocabulary: `read`
 * gates reads, `write` gates content mutation, and `manage` gates
 * administration of the domain itself (configure, delete, change access) —
 * a distinct tier, never a respelling of `write`. A domain that needs a
 * verb of its own beyond these (`members:invite`, `agents:run`,
 * `plugins:install`) names it and declares `mutates` for it; read
 * implication is derived from `mutates`, so it works for every verb.
 */
export const PERMISSION_READ_ACTION = 'read';
export const PERMISSION_WRITE_ACTION = 'write';
export const PERMISSION_MANAGE_ACTION = 'manage';

/** The `<domain>` half of a slug — `'agents'` for `'agents:write'`. */
export function permissionDomain(slug: string): string {
  return slug.slice(0, slug.indexOf(':'));
}

/** The `<action>` half of a slug — `'write'` for `'agents:write'`. */
export function permissionAction(slug: string): string {
  return slug.slice(slug.indexOf(':') + 1);
}

/**
 * Slugs this codebase renamed, old spelling to new. `runSeeds` rewrites
 * existing catalog rows and role grants through this map on every boot, so
 * an install upgraded across the rename keeps the access its custom roles
 * were given rather than silently losing it.
 *
 * Unlike a retirement, a rename is not a capability change: the new slug
 * gates exactly what the old one did. Entries stay here permanently —
 * removing one strands any install that has not booted since.
 */
export const RENAMED_PERMISSION_SLUGS: Readonly<Record<string, string>> = {
  // Spelling and structure only — a rename never changes the verb. The verb
  // tiers (`manage` vs `write` vs a domain's own verb) are capability
  // distinctions the catalog keeps.
  // `feature_flags:manage` is not here: its target `feature-flags:manage` is
  // retired (see RETIRED_PERMISSION_SLUGS in the server), and a rename onto a
  // retired slug would carry grants forward only for the retire pass to drop
  // them. Both spellings are retired instead.
  'service_accounts:read': 'service-accounts:read',
  'service_accounts:write': 'service-accounts:write',
  // Plugin-contributed slugs. The host owns the map because the rewrite
  // runs in `runSeeds`, before any plugin is registered.
  'network_policies:read': 'network-policies:read',
  'network_policies:write': 'network-policies:write',
  // Two colons broke the `<domain>:<action>` shape. The action-encodes-
  // sub-resource form keeps the projects domain and names the action
  // precisely: `projects:manage-members`.
  'projects:members:manage': 'projects:manage-members',
  // Installs that already ran the first rename pick up the final spelling.
  'project-members:manage': 'projects:manage-members',
};
